import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWiziQuizById } from '../components/apiClient';

interface WiziQuiz {
  id: number;
  name: string;
  description: string;
  cover_image: string | null;
  total_marks: number;
  passing_score: number;
  time_limit: number;
  level: string;
  status: string;
  quiz_order: number;
  instructions: string;
  is_published: boolean;
}

const MockQuizDetails: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState<WiziQuiz | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const defaultCoverImage = 'https://via.placeholder.com/1200x400?text=Mock+Quiz';

  useEffect(() => {
    const fetchQuizDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getWiziQuizById(parseInt(quizId!));
        if (response && response.result) {
          setQuiz(response.result);
        } else {
          setError('Failed to load quiz details');
        }
      } catch (err) {
        console.error('Failed to fetch quiz details:', err);
        setError('Failed to load quiz details. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (quizId) {
      fetchQuizDetails();
    }
  }, [quizId]);

  const handleStartQuiz = () => {
    alert('Quiz taking functionality will be implemented in the next phase. Stay tuned!');
  };

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'elementary': return 'success';
      case 'moderate': return 'warning';
      case 'advance': return 'danger';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '80px' }}>
        <div className="container py-5">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="text-muted mt-3">Loading quiz details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !quiz) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '80px' }}>
        <div className="container py-5">
          <div className="alert alert-danger border-0" style={{ borderRadius: '15px' }}>
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error || 'Quiz not found'}
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/mock-quiz')}
            style={{ borderRadius: '10px' }}
          >
            <i className="fas fa-arrow-left me-2"></i>
            Back to Mock Tests
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9ff', paddingTop: '80px' }}>
      <div className="container py-5">
        {/* Back Button */}
        <button 
          className="btn btn-outline-primary mb-4"
          onClick={() => navigate('/mock-quiz')}
          style={{ borderRadius: '10px' }}
        >
          <i className="fas fa-arrow-left me-2"></i>
          Back to Mock Tests
        </button>

        {/* Cover Image */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px', overflow: 'hidden' }}>
          <img 
            src={quiz.cover_image || defaultCoverImage} 
            className="card-img-top" 
            alt={quiz.name}
            style={{ height: '350px', objectFit: 'cover' }}
          />
          <div className="position-absolute top-0 end-0 m-3">
            <span 
              className={`badge bg-${getLevelColor(quiz.level)}`}
              style={{ fontSize: '1rem', padding: '10px 20px', borderRadius: '10px' }}
            >
              {quiz.level}
            </span>
          </div>
        </div>

        <div className="row g-4">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Quiz Title */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
              <div className="card-body p-4">
                <h1 className="fw-bold mb-3">
                  <i className="fas fa-clipboard-list me-3 text-primary"></i>
                  {quiz.name}
                </h1>
                {quiz.description && (
                  <p className="text-muted lead mb-0">{quiz.description}</p>
                )}
              </div>
            </div>

            {/* Instructions */}
            {quiz.instructions && (
              <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                <div className="card-body p-4">
                  <h4 className="fw-bold mb-3">
                    <i className="fas fa-list-ul me-2 text-primary"></i>
                    Instructions
                  </h4>
                  <div 
                    className="text-muted" 
                    style={{ 
                      whiteSpace: 'pre-wrap', 
                      lineHeight: '1.8',
                      fontSize: '1rem'
                    }}
                  >
                    {quiz.instructions}
                  </div>
                </div>
              </div>
            )}

            {/* Performance Tracking Placeholder */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: '#e3f2fd' }}>
              <div className="card-body p-4">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-chart-line me-2 text-info"></i>
                  Your Performance History
                </h5>
                <div className="alert alert-info border-0 mb-0" style={{ borderRadius: '12px', backgroundColor: 'white' }}>
                  <i className="fas fa-info-circle me-2"></i>
                  Performance tracking will be available once you attempt this quiz. Your scores, time taken, and detailed analytics will appear here.
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">
            {/* Quick Stats */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
              <div className="card-body p-4">
                <h5 className="fw-bold mb-4">Quiz Details</h5>
                
                <div className="mb-3 p-3" style={{ backgroundColor: '#fff3cd', borderRadius: '10px' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-star text-warning fs-4 me-3"></i>
                    <div>
                      <small className="text-muted d-block">Total Marks</small>
                      <strong className="fs-5">{quiz.total_marks}</strong>
                    </div>
                  </div>
                </div>

                <div className="mb-3 p-3" style={{ backgroundColor: '#d1f2eb', borderRadius: '10px' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-check-circle text-success fs-4 me-3"></i>
                    <div>
                      <small className="text-muted d-block">Passing Score</small>
                      <strong className="fs-5">{quiz.passing_score}</strong>
                    </div>
                  </div>
                </div>

                <div className="mb-3 p-3" style={{ backgroundColor: '#d1ecf1', borderRadius: '10px' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-clock text-info fs-4 me-3"></i>
                    <div>
                      <small className="text-muted d-block">Duration</small>
                      <strong className="fs-5">{quiz.time_limit} minutes</strong>
                    </div>
                  </div>
                </div>

                <div className="mb-4 p-3" style={{ backgroundColor: '#f8d7da', borderRadius: '10px' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-signal text-danger fs-4 me-3"></i>
                    <div>
                      <small className="text-muted d-block">Difficulty Level</small>
                      <strong className="fs-5">{quiz.level}</strong>
                    </div>
                  </div>
                </div>

                <button 
                  className="btn btn-primary w-100 py-3"
                  onClick={handleStartQuiz}
                  style={{ borderRadius: '10px', fontSize: '1.1rem' }}
                >
                  <i className="fas fa-play me-2"></i>
                  Start Quiz
                </button>
              </div>
            </div>

            {/* Preparation Tips */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '15px', backgroundColor: '#f0f7ff' }}>
              <div className="card-body p-4">
                <h5 className="fw-bold mb-3">
                  <i className="fas fa-lightbulb me-2 text-warning"></i>
                  Preparation Tips
                </h5>
                <ul className="list-unstyled mb-0">
                  <li className="mb-2">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    Read all instructions carefully
                  </li>
                  <li className="mb-2">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    Ensure stable internet connection
                  </li>
                  <li className="mb-2">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    Keep {quiz.time_limit} minutes free
                  </li>
                  <li className="mb-0">
                    <i className="fas fa-check-circle text-success me-2"></i>
                    Find a quiet environment
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockQuizDetails;
